import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isToday,
  isBefore,
} from "date-fns";
import { ChevronLeft, ChevronRight, Calendar, ChevronDown } from "lucide-react";
import styles from "../styles/DatePicker.module.css";

interface CustomSelectProps {
  value: string | number;
  options: { label: string | number; value: any }[];
  onChange: (value: any) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  options,
  onChange,
  isOpen,
  onToggle,
  onClose,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const selectedEl = dropdownRef.current.querySelector(
        `.${styles.selectedOption}`,
      );
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: "center" });
      }
    }
  }, [isOpen]);

  return (
    <div className={styles.customSelectContainer} ref={dropdownRef}>
      <div
        className={styles.customSelectTrigger}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {value}
        <ChevronDown size={14} />
      </div>
      {isOpen && (
        <div className={styles.customSelectDropdown}>
          {options.map((option) => (
            <div
              key={option.value}
              className={`${styles.customSelectOption} ${
                option.label === value ? styles.selectedOption : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onChange(option.value);
                onClose();
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface DatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
  minDate?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  minDate,
  style,
  inputStyle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [openSelect, setOpenSelect] = useState<"month" | "year" | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popupHeight = 310;
      const spaceBelow = window.innerHeight - rect.bottom;

      let top = rect.bottom + 4;
      if (spaceBelow < popupHeight && rect.top > popupHeight) {
        top = rect.top - popupHeight;
      }

      setPopupPosition({
        top: top,
        left: rect.left,
      });
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) {
      setOpenSelect(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      const [year, month, day] = value.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      setSelectedDate(date);
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  useEffect(() => {
    if (value) {
      const [year, month, day] = value.split("-").map(Number);
      setCurrentMonth(new Date(year, month - 1, day));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const handleDateClick = (day: Date) => {
    if (minDate) {
      const [minYear, minMonth, minDay] = minDate.split("-").map(Number);
      const minDateObj = new Date(minYear, minMonth - 1, minDay);
      minDateObj.setHours(0, 0, 0, 0);
      const dayCheck = new Date(day);
      dayCheck.setHours(0, 0, 0, 0);

      if (isBefore(dayCheck, minDateObj)) return;
    }

    onChange(format(day, "yyyy-MM-dd"));
    setIsOpen(false);
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 120 }, (_, i) => currentYear - 100 + i);
  };

  const handleMonthChange = (monthIndex: number) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(monthIndex);
    setCurrentMonth(newDate);
    setOpenSelect(null);
  };

  const handleYearChange = (year: number) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(year);
    setCurrentMonth(newDate);
    setOpenSelect(null);
  };

  const renderHeader = () => {
    const monthOptions = months.map((month, index) => ({
      label: month,
      value: index,
    }));

    const yearOptions = generateYears().map((year) => ({
      label: year,
      value: year,
    }));

    return (
      <div className={styles.header}>
        <button
          onClick={prevMonth}
          className={styles.navButton}
          type="button"
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>
        <div className={styles.headerDropdowns}>
          <CustomSelect
            value={months[currentMonth.getMonth()]}
            options={monthOptions}
            onChange={handleMonthChange}
            isOpen={openSelect === "month"}
            onToggle={() =>
              setOpenSelect(openSelect === "month" ? null : "month")
            }
            onClose={() => setOpenSelect(null)}
          />
          <CustomSelect
            value={currentMonth.getFullYear()}
            options={yearOptions}
            onChange={handleYearChange}
            isOpen={openSelect === "year"}
            onToggle={() =>
              setOpenSelect(openSelect === "year" ? null : "year")
            }
            onClose={() => setOpenSelect(null)}
          />
        </div>
        <button
          onClick={nextMonth}
          className={styles.navButton}
          type="button"
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    );
  };

  const renderDays = () => {
    const days = [];
    const dateFormat = "EEE";
    const startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className={styles.dayName} key={i}>
          {format(addDays(startDate, i), dateFormat).charAt(0)}
        </div>,
      );
    }

    return <div className={styles.daysGrid}>{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, "d");
        const cloneDay = day;

        let isDisabled = false;
        if (minDate) {
          const [minYear, minMonth, minDay] = minDate.split("-").map(Number);
          const minDateObj = new Date(minYear, minMonth - 1, minDay);
          minDateObj.setHours(0, 0, 0, 0);

          const dayCheck = new Date(day);
          dayCheck.setHours(0, 0, 0, 0);

          if (isBefore(dayCheck, minDateObj)) isDisabled = true;
        }

        const isSelected = selectedDate && isSameDay(day, selectedDate);
        const isCurrentMonth = isSameMonth(day, monthStart);

        days.push(
          <div
            className={`${styles.dayCell} 
              ${!isCurrentMonth ? styles.outsideMonth : ""} 
              ${isSelected ? styles.selected : ""}
              ${isToday(day) ? styles.today : ""}
              ${isDisabled ? styles.disabled : ""}
            `}
            key={day.toString()}
            onClick={() => !isDisabled && handleDateClick(cloneDay)}
          >
            <span>{formattedDate}</span>
          </div>,
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div
          className={styles.weekRow}
          style={{ display: "contents" }}
          key={day.toString()}
        >
          {days}
        </div>,
      );
      days = [];
    }
    return <div className={styles.calendarBody}>{rows}</div>;
  };

  return (
    <div className={`${styles.form_input}`} style={style} ref={containerRef}>
      <div className={styles.form_input__container} onClick={toggleOpen}>
        <input
          type="text"
          aria-label="Select date"
          className={`${styles.form_input__box}`}
          style={inputStyle}
          value={selectedDate ? format(selectedDate, "MM/dd/yyyy") : ""}
          placeholder={placeholder}
          readOnly
        />
        <Calendar className={styles.form_input__icon} size={10} />
      </div>

      {isOpen &&
        createPortal(
          <div
            className={styles.calendarPopup}
            aria-label="Calendar"
            style={{
              position: "fixed",
              top: popupPosition.top,
              left: popupPosition.left,
              ...style,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {renderHeader()}
            {renderDays()}
            {renderCells()}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DatePicker;
